import { createBlock, createNote, SYSTEM_TAB_IDS } from '../../lib/note-factories';
import type { ClassroomSessionDocument } from '../../types/sync';
import type { KnowledgeNoteDocument, NoteBlockKind } from '../../types/note';
import type { FullSummary } from './classroom-ai';

/** 与 iOS KnowledgeNoteComposition.stableID 和服务端课堂总结身份一致。 */
export async function classroomSummaryNoteID(sessionID: string) {
  if (!/^[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(sessionID)) throw new Error('课堂身份无效');
  const namespace = new Uint8Array(sessionID.replaceAll('-', '').match(/../g)!.map(pair => parseInt(pair, 16)));
  const name = new TextEncoder().encode('classmind.composition.v1/tab/classroomSummary');
  const input = new Uint8Array(namespace.length + name.length); input.set(namespace); input.set(name, namespace.length);
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-1', input)).slice(0, 16);
  hash[6] = (hash[6]! & 15) | 80; hash[8] = (hash[8]! & 63) | 128;
  const hex = Array.from(hash, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export async function fullSummaryNote(session: ClassroomSessionDocument, result: FullSummary, sourceFingerprint: string): Promise<KnowledgeNoteDocument> {
  const note = createNote({ tabID: SYSTEM_TAB_IDS.courses, courseID: session.courseID }, `${session.title} · 课堂总结`);
  note.id = await classroomSummaryNoteID(session.id);
  note.blocks = [];
  const append = (kind: NoteBlockKind, text: string, detailText?: string) => note.blocks.push({ ...createBlock(kind), text, detailText, orderIndex: note.blocks.length });
  const sources = new Map(session.knowledgeBlocks.map(block => [block.id.toLowerCase(), { block, anchor: crypto.randomUUID() }]));
  if (!/^[a-f0-9]{64}$/.test(sourceFingerprint)) throw new Error('总结发布指纹无效');
  append('heading1', result.title, JSON.stringify({ kind: 'classroom-summary-publication', version: 1, sessionID: session.id, sourceFingerprint })); append('paragraph', result.overview);
  for (const section of result.sections) {
    append('heading2', section.title); append('paragraph', section.body);
    for (const source of section.sourceIDs) {
      const entry = sources.get(source.toLowerCase());
      if (entry) append('bookmark', `classmind://note/${note.id}?block=${entry.anchor}`, `查看来源：${entry.block.title}`);
    }
  }
  for (const [title, values] of [['内容校正', result.corrections], ['排除的无关内容', result.excluded]] as const) {
    if (values.length) { append('heading2', title); values.forEach(value => append('bulletList', value)); }
  }
  append('heading2', '来源记录');
  for (const { block, anchor } of sources.values()) {
    note.blocks.push({ ...createBlock('heading2'), id: anchor, text: block.title, orderIndex: note.blocks.length });
    append('paragraph', block.polishedTranscript || block.sourceText);
    if (block.polishedTranscript && block.polishedTranscript !== block.sourceText) {
      append('paragraph', `原始转录：\n${block.sourceText}`);
    }
  }
  return note;
}
